import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import TemplateEditor from '../components/qptemplates/TemplateEditor';
import { confirmDelete, confirmAction } from '../utils/confirm';

function QPTemplateLibrary() {
  const { user } = useAuth();
  const tenantId = user?.tenantId;

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const searchTimer = useRef();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editTemplateId, setEditTemplateId] = useState(null);

  const loadTemplates = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const res = await api.get(`/qp-templates/${tenantId}`, {
        params: search ? { search } : {},
      });
      if (res.data.success) setTemplates(res.data.data || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [tenantId, search]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(val), 400);
  };

  const handleCreate = () => {
    setEditTemplateId(null);
    setEditorOpen(true);
  };

  const handleEdit = (t) => {
    setEditTemplateId(t.TemplateID);
    setEditorOpen(true);
  };

  const handleDelete = async (t) => {
    if (!(await confirmDelete(`template "${t.TemplateName}"`))) return;
    try {
      await api.delete(`/qp-templates/${t.TemplateID}`);
      toast.success('Template deleted');
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete template');
    }
  };

  const handleDuplicate = async (t) => {
    const ok = await confirmAction(
      `Duplicate "${t.TemplateName}"?`,
      'A new template will be created with the same structure.',
      { confirmText: 'Yes, duplicate', icon: 'question', confirmColor: '#1a237e' }
    );
    if (!ok) return;
    try {
      const res = await api.post(`/qp-templates/${t.TemplateID}/duplicate`);
      if (res.data.success) {
        toast.success('Template duplicated');
        loadTemplates();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to duplicate template');
    }
  };

  const formatDate = (val) => {
    if (!val) return '—';
    return new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (editorOpen) {
    return (
      <TemplateEditor
        tenantId={tenantId}
        templateId={editTemplateId}
        onClose={() => setEditorOpen(false)}
        onSaved={() => {
          setEditorOpen(false);
          loadTemplates();
        }}
      />
    );
  }

  return (
    <div className="page-card">
      <div
        className="page-card-header"
        style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: 16 }}
      >
        <h3 style={{ margin: 0 }}>
          QP Template Library
          {templates.length > 0 && (
            <span style={{ fontWeight: 400, fontSize: 14, color: '#616161', marginLeft: 8 }}>
              ({templates.length})
            </span>
          )}
        </h3>
        <button className="btn btn-add" onClick={handleCreate}>+ Create Template</button>
      </div>

      <div className="alloc-toolbar" style={{ flexWrap: 'wrap' }}>
        <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 240 }}>
          <label>Search</label>
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by template name or description..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No templates found. Click "Create Template" to add one.
        </div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Template Name</th>
              <th style={{ textAlign: 'center' }}>Sections</th>
              <th style={{ textAlign: 'center' }}>Questions</th>
              <th style={{ textAlign: 'center' }}>Total Marks</th>
              <th>Created Date</th>
              <th>Created By</th>
              <th style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.TemplateID}>
                <td>
                  <strong>{t.TemplateName}</strong>
                  {t.Description && (
                    <div style={{ fontSize: 12, color: '#9e9e9e', marginTop: 2 }}>{t.Description}</div>
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>{t.TotalSections}</td>
                <td style={{ textAlign: 'center' }}>{t.TotalQuestions}</td>
                <td style={{ textAlign: 'center' }}>
                  {t.TotalSetMarks}
                  {t.MaxMarks ? (
                    <span style={{ color: '#9e9e9e' }}> / {t.MaxMarks}</span>
                  ) : null}
                </td>
                <td style={{ color: '#616161', fontSize: 13 }}>{formatDate(t.CreatedAt)}</td>
                <td style={{ color: '#616161', fontSize: 13 }}>{t.CreatedByName || '—'}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="action-btn action-edit"
                      title="Edit"
                      onClick={() => handleEdit(t)}
                    >✏</button>
                    <button
                      className="action-btn"
                      title="Duplicate"
                      onClick={() => handleDuplicate(t)}
                      style={{ background: '#e3f2fd', color: '#1565c0' }}
                    >⎘</button>
                    <button
                      className="action-btn action-delete"
                      title="Delete"
                      onClick={() => handleDelete(t)}
                    >🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default QPTemplateLibrary;
